export const UNLOCKED_CATEGORY_IDS = [
  'skull_bones',
  'face_bones',
  'trunk_bones'
];

export const MAX_GUEST_MORPHEMES = 24;
export const MAX_BASIC_MORPHEMES = 100;

export const UNLOCKED_GAMES = [
  'flashcards',
  'match'
];

export const LOCKED_GAMES = [
  'quiz',
  'morpheme'
];

export const PAST_DUE_GRACE_PERIOD_MS = 5 * 24 * 60 * 60 * 1000; // 5 tam gün ek süre

/**
 * Kullanıcının ödeme gecikmesi durumunu ve 5 günlük ek süresini kontrol eder
 */
export const getPastDueState = (userData) => {
  if (!userData) return { isPastDue: false, isWithinGracePeriod: false, daysLeft: 0 };
  const status = (userData.subscriptionStatus || '').toLowerCase();
  if (status !== 'past_due') return { isPastDue: false, isWithinGracePeriod: false, daysLeft: 0 };

  const pastDueSince = userData.pastDueSince ? new Date(userData.pastDueSince).getTime() : Date.now();
  const elapsed = Math.max(0, Date.now() - pastDueSince);
  const remainingMs = Math.max(0, PAST_DUE_GRACE_PERIOD_MS - elapsed);
  const isWithinGracePeriod = remainingMs > 0;
  const daysLeft = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));

  return {
    isPastDue: true,
    isWithinGracePeriod,
    daysLeft,
    pastDueSince
  };
};

/**
 * Kullanıcının Temel planda olup olmadığını belirler
 */
export const checkIsBasic = (userData) => {
  if (!userData) return false;
  if (userData.isLifetime === true) return false;

  const planStr = (userData.plan || '').toLowerCase();
  const status = (userData.subscriptionStatus || '').toLowerCase();

  const isBasicFlag =
    userData.isBasic === true ||
    userData.planType === 'basic' ||
    planStr.includes('basic') ||
    planStr.includes('temel') ||
    status === 'basic';

  if (!isBasicFlag) return false;

  // İptal edilmiş veya ücretsiz statüsüne düşmüşse Temel plan geçerli değildir
  if (status === 'canceled' || status === 'free') return false;

  // Karttan çekim başarısız / gecikmede (past_due) ise 5 günlük ek süre kuralı
  if (status === 'past_due') {
    const { isWithinGracePeriod } = getPastDueState(userData);
    return isWithinGracePeriod;
  }

  // Deneme süresinde ise (trialing veya isTrial bayrağı): süresi bitmiş mi kontrol et
  // status 'active' olan ücretli aboneler deneme kontrolüne sokulmaz
  if (status !== 'active' && (status === 'trialing' || userData.isTrial === true || userData.isTrialing === true || !!userData.trialEndDate)) {
    let endMs = null;
    if (userData.trialEndDate) {
      if (typeof userData.trialEndDate.toDate === 'function') {
        endMs = userData.trialEndDate.toDate().getTime();
      } else if (typeof userData.trialEndDate === 'number') {
        endMs = userData.trialEndDate;
      } else if (userData.trialEndDate.seconds) {
        endMs = userData.trialEndDate.seconds * 1000;
      } else {
        const parsed = new Date(userData.trialEndDate).getTime();
        if (!isNaN(parsed)) endMs = parsed;
      }
    } else if (userData.trialStartDate) {
      const start = new Date(userData.trialStartDate).getTime();
      if (!isNaN(start)) endMs = start + (3 * 24 * 60 * 60 * 1000);
    }

    // Deneme süresi dolmuşsa ve ücret alınamamışsa Temel plan sona ermiştir
    if (endMs && Date.now() > endMs) {
      return false;
    }
  }

  // Aktif veya geçerli deneme durumunda Temel plan geçerlidir
  if (status === 'active' || status === 'trialing' || status === 'basic') {
    return true;
  }

  return userData.isBasic === true;
};

/**
 * Kullanıcının Pro (Tam erişim) durumunda olup olmadığını belirler
 */
export const checkIsPro = (userData) => {
  if (!userData) return false;

  // Ömür boyu VIP
  if (userData.isLifetime === true) return true;

  // Temel plan ise Pro değildir
  if (userData.isBasic === true) return false;

  const status = (userData.subscriptionStatus || '').toLowerCase();

  // Kart gecikmesi (past_due) - 5 günlük ek süre kuralı
  if (status === 'past_due') {
    const { isWithinGracePeriod } = getPastDueState(userData);
    return isWithinGracePeriod && userData.isPro === true;
  }

  // İptal edilmiş veya süresi bitmiş
  if (status === 'canceled' || status === 'free') return false;

  // Paddle deneme (trialing) veya aktif (active) abonelik
  if (status === 'active' || status === 'pro' || status === 'trialing') {
    return userData.isPro === true || !userData.isBasic;
  }

  if (userData.isPro === true) return true;
  return false;
};

/**
 * Kategorinin kullanıcı için açık olup olmadığını kontrol eder
 */
export const isCategoryUnlocked = (categoryId, isPro) => {
  if (isPro) return true;
  if (!categoryId || categoryId === 'all') return false;
  return UNLOCKED_CATEGORY_IDS.includes(categoryId);
};

/**
 * Morfemin kullanıcı için açık olup olmadığını kontrol eder:
 * - Pro/Ömür Boyu: tüm morfemler (571+) açık
 * - Temel plan (isBasic true): ilk 100 morfem açık
 * - Misafir (giriş yapmamış): ilk 24 morfem açık
 */
export const isMorphemeUnlocked = (index, isPro, isBasic = false) => {
  if (isPro) return true;
  if (isBasic) return index < MAX_BASIC_MORPHEMES;
  return index < MAX_GUEST_MORPHEMES;
};

/**
 * Kullanıcının herhangi bir ücretli plana (Temel, Pro veya Ömür Boyu) sahip olup olmadığını kontrol eder.
 * Giriş yapmamış misafirler veya hesap oluşturup henüz bir plan satın almamış (ücretsiz) kullanıcılar false döner.
 */
export const checkHasPaidPlan = (userData) => {
  if (!userData) return false;
  const preview = getPreviewRole();
  if (preview === 'pro' || preview === 'basic') return true;
  return checkIsBasic(userData) || checkIsPro(userData) || userData.isLifetime === true;
};

/**
 * Oyun modunun kullanıcı için açık olup olmadığını kontrol eder:
 * - Pro / Ömür Boyu: Tüm oyunlar açık (Flashcard, Eşleştirme, Quiz, Morfem)
 * - Temel plan (hasPlan: true): Flashcard & Eşleştirme açık, Quiz & Morfem kapalı
 * - Misafir veya Plan almamış ücretsiz kullanıcı (hasPlan: false): Yalnızca Flashcard açık, Eşleştirme, Quiz & Morfem kapalı
 */
export const isGameUnlocked = (gameId, isPro, hasPlan = false) => {
  if (isPro) return true;
  if (gameId === 'flashcards') return true;
  if (gameId === 'match') return Boolean(hasPlan);
  return false;
};

/**
 * Oyun modunun misafir (giriş yapmamış) kullanıcı için açık olup olmadığını kontrol eder.
 * Misafir kullanıcılar yalnızca Flashcard moduna (günlük 5 oyun kotası ile) erişebilir;
 * Eşleştirme, Quiz ve Morfem oyunları misafir kullanıcılara kapalıdır.
 */
export const isGameUnlockedForGuest = (gameId) => {
  return gameId === 'flashcards';
};

/**
 * Test / Preview rolü:
 * Geliştirme/test kolaylığı sağlamak için URL parametresi (?previewRole=pro/basic) veya localStorage'dan okunur.
 * GÜVENLİK KURALI:
 * Canlı / üretim ortamında (production build veya canlı domain) TAMAMEN devre dışıdır, her zaman null döner.
 * Yalnızca localhost ortamında geliştirme/test amaçlı çalışır.
 */
export const getPreviewRole = () => {
  if (typeof window === 'undefined') return null;

  // 1. Production build ortamında kesinlikle devre dışı
  if (process.env.NODE_ENV === 'production') return null;

  // 2. Canlı domain / IP kontrolü (yalnızca yerel geliştirme ortamında izin verilir)
  try {
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      hostname === '0.0.0.0';

    if (!isLocalhost) return null;

    return (
      new URLSearchParams(window.location.search).get('previewRole') ||
      localStorage.getItem('healthlex_preview_role') ||
      null
    );
  } catch (e) {
    return null;
  }
};
